import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Capacitor } from '@capacitor/core';
import apiService from './api';

/**
 * Over-the-air ("live") updates for the Capacitor app.
 *
 * The native shell only changes via a store release. The web bundle it loads —
 * the compiled Vite SPA — can be swapped at runtime, so bug fixes and UI
 * changes reach the field in minutes rather than days.
 *
 * Store policy: only functionality the reviewers already saw may be changed
 * this way (Apple guideline 3.3.2, Google Play's equivalent). Anything that
 * touches android/, ios/, capacitor.config.ts, or adds a plugin still needs a
 * store release — and its `native_min_version` bumped so old shells are pinned.
 *
 * The plugin runs in manual mode (autoUpdate: false in capacitor.config.ts):
 * we ask our own backend through the authenticated api client instead of
 * letting the plugin POST to a Capgo-shaped endpoint. That keeps the JWT,
 * refresh handling and base-URL config in one place.
 */

const CHANNEL = import.meta.env.VITE_LIVE_UPDATE_CHANNEL || 'production';

// Don't hammer the endpoint — foreground events can fire in quick succession
// (e.g. dismissing a permission dialog). One check per 15 minutes is plenty.
const MIN_CHECK_INTERVAL_MS = 15 * 60 * 1000;

let lastCheckedAt = 0;
let checkInFlight = false;

interface UpdateCheckResponse {
  available: boolean;
  version?: string;
  url?: string;
  checksum?: string;
  release_notes?: string | null;
}

/**
 * Tell the plugin this bundle booted successfully.
 *
 * MUST be called on every launch. If it doesn't fire within `appReadyTimeout`
 * (10s, set in capacitor.config.ts) the plugin assumes the bundle is broken and
 * rolls back to the last known-good one. That rollback is the whole safety net
 * behind shipping without review, so this call is deliberately the first thing
 * main.tsx does and is never gated behind auth, routing or network.
 *
 * No-op on web.
 */
export async function notifyAppReady(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch (err) {
    console.warn('[liveUpdate] notifyAppReady failed:', err);
  }
}

/**
 * Check for a newer bundle and stage it for the next cold start.
 *
 * Staged, not applied: `next()` rather than `set()`. `set()` reloads the
 * WebView immediately, which would throw away unsaved form state — losing a
 * half-entered timesheet or spray record mid-shift. The update lands silently
 * the next time the app is launched from cold.
 *
 * Every failure path is swallowed. A failed update check must never degrade
 * the running app.
 */
export async function checkForUpdate(options?: { force?: boolean }): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const now = Date.now();
  if (!options?.force && now - lastCheckedAt < MIN_CHECK_INTERVAL_MS) return;
  if (checkInFlight) return;

  checkInFlight = true;
  try {
    // `current()` reports both the running bundle and the native shell version,
    // so there's no need to ask @capacitor/app separately. Note the native
    // version must come from here, not from the build-time __APP_VERSION__ —
    // after an OTA update those two diverge.
    const current = await CapacitorUpdater.current();

    const res = await apiService.get<UpdateCheckResponse>('/app-updates/check', {
      params: {
        channel: CHANNEL,
        platform: Capacitor.getPlatform(),
        native_version: current.native,
        current_bundle: current.bundle.version,
      },
    });

    lastCheckedAt = Date.now();

    if (!res.success) {
      console.warn('[liveUpdate] check rejected:', res.error.message);
      return;
    }

    const update = res.data;
    if (!update.available || !update.url || !update.version) return;

    console.info(`[liveUpdate] downloading bundle ${update.version}`);
    const bundle = await CapacitorUpdater.download({
      url: update.url,
      version: update.version,
      checksum: update.checksum,
    });

    await CapacitorUpdater.next({ id: bundle.id });
    console.info(`[liveUpdate] bundle ${update.version} staged for next launch`);
  } catch (err) {
    console.warn('[liveUpdate] check failed:', err);
  } finally {
    checkInFlight = false;
  }
}
